import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { styles, isNumber } from 'yankees-react-utils';
import Card from 'react-bootstrap/Card';
import { NavLink } from 'react-router-dom';
import { chatamsBlue } from '../styles/theme';
import { useBlobDownloadQuery } from '../data';

export const navCardImgWidth = '33%';
export const navCardHeaderHeight = '30px';
export const hoverColor = styles.pSBC(-0.1, '#e6e6e6');
const StyledNavCardContent = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    > .card-title {
        background-color: ${styles.theme.background.geyser};
        color: ${styles.theme.fontColors.mineShaft};
        text-transform: uppercase;
        margin: 0;
        font-family: ${styles.theme.font.body};
        padding: 8px 0 6px 1.25rem;
        font-weight: bold !important;
        letter-spacing: .5px;
        height: ${navCardHeaderHeight};
    }
    .card-body {
        .card-title {
            font-size: 1.75em;
            line-height: 1;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        }
        .card-subtitle {
            color: ${styles.theme.fontColors.doveGray};
            font-size: 1em;
        }
    }
`;
export const StyledNavCard = styled(Card)`
    position: relative;
    && {
        flex-direction: row;
        flex-wrap: nowrap;
        height: ${({ $height }) => (isNumber($height) ? `${$height}px` : $height)};
        box-shadow: 0px 3px 3px -2px #97969B;
        color: ${chatamsBlue};
    }
    &::after {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }
    &:active::after {
        box-shadow: inset 0 0px 5px 0px #97969B;
    }
    &:hover {
        cursor: pointer;
        .card-body {
            background-color: ${hoverColor};
        }
    }
    .card-img {
        width: ${navCardImgWidth};
        flex-grow: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        object-fit: ${({ $logo }) => ($logo ? 'contain' : 'cover')};
        ${({ $logo, $imgBackground }) => ($logo ? `padding: ${$imgBackground ? '10px' : '5px'};` : '')}
        ${({ $imgBackground }) => ($imgBackground ? `background-color: ${$imgBackground};` : '')}
    }
`;
const StyledNavCardChild = styled.div`
    position: absolute;
`;
const StyledNavCardContainer = styled.div`
    position: relative;
    > a:hover {
        text-decoration: none;
    }
    ${({ $disabled }) => ($disabled ? `
        opacity: 0.65;
        pointer-events: none;
    ` : '')}
`;

const NavCard = ({ title, subTitle, header, route, imgSrc, imgAs, imgBackground, height, children, childrenStyle, disabled }) => {
    const { src } = useBlobDownloadQuery(imgSrc);
    return (
        <StyledNavCardContainer $disabled={disabled}>
            <NavLink to={route}>
                <StyledNavCard $logo={imgSrc?.endsWith('logo')} $imgBackground={imgBackground} $height={height}>
                    <StyledNavCard.Img src={src} as={imgAs} />
                    <StyledNavCardContent>
                        {header && <StyledNavCard.Title>{header}</StyledNavCard.Title>}
                        <StyledNavCard.Body>
                            <StyledNavCard.Title>{title}</StyledNavCard.Title>
                            {subTitle && <StyledNavCard.Subtitle>{subTitle}</StyledNavCard.Subtitle>}
                        </StyledNavCard.Body>
                    </StyledNavCardContent>
                </StyledNavCard>
            </NavLink>
            {children && <StyledNavCardChild style={childrenStyle}>{children}</StyledNavCardChild>}
        </StyledNavCardContainer>
    );
};

NavCard.propTypes = {
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.string,
    header: PropTypes.string,
    route: PropTypes.string.isRequired,
    imgSrc: PropTypes.string,
    imgAs: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    imgBackground: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    childrenStyle: PropTypes.shape({}),
    disabled: PropTypes.bool,
};
NavCard.defaultProps = {
    subTitle: '',
    header: '',
    imgBackground: '',
    height: 120,
    children: undefined,
    childrenStyle: undefined,
    imgSrc: undefined,
    imgAs: undefined,
    disabled: false,
};

export default NavCard;
